import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { publicSignup, formatApiError } from "@/lib/api";
import { COMPLEXITY_ITEMS } from "@/data/content";
import { maskPhone, validatePhone } from "@/lib/format";
import { EASE } from "@/lib/motion";
import StatePicker from "@/components/StatePicker";

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted";

export default function PortalSignupForm({ initialItems = [], initialStates = [], heading, subheading }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", dependents: 0 });
  const [items, setItems] = useState(initialItems);
  const [states, setStates] = useState(initialStates);
  const [tos, setTos] = useState(false);
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleItem = (id) => setItems((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const onSubmit = async (e) => {
    e.preventDefault();
    const phoneError = validatePhone(form.phone, { required: false });
    if (phoneError) return toast.error(phoneError);
    if (!tos) return toast.error("Please accept the Terms of Service and Privacy Policy to continue.");
    setStatus("sending");
    try {
      const data = await publicSignup({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        dependents: Number(form.dependents) || 0,
        selected_items: items,
        states,
        tos_accepted: true,
      });
      setResult(data);
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  if (status === "done") {
    return (
      <motion.div
        data-testid="signup-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="border border-steel-brass/40 bg-steel-surface p-10"
      >
        <div className="flex h-12 w-12 items-center justify-center border border-steel-brass/50">
          <Check className="h-5 w-5 text-steel-brass" />
        </div>
        <h3 className="mt-6 font-display text-2xl font-medium text-steel-text">
          Your portal is ready, {form.name.split(" ")[0]}.
        </h3>
        {result?.email_sent ? (
          <p className="mt-3 text-sm leading-relaxed text-steel-secondary">
            We've emailed <span className="text-steel-text">{form.email}</span> a temporary password. Sign in, set
            your own password, then confirm your details — takes about two minutes.
          </p>
        ) : (
          <p className="mt-3 text-sm leading-relaxed text-amber-300">
            Your account was created, but we couldn't send the sign-in email
            {result?.email_error ? `: ${result.email_error}` : ""}. Call or message us and we'll get you your
            temporary password.
          </p>
        )}
        {result?.estimate && (
          <div className="mt-6 border border-steel-border bg-steel-elevated p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">Preliminary estimate</p>
            <p className="mt-1.5 font-display text-2xl font-medium text-steel-brass" data-testid="signup-estimate">
              ${Number(result.estimate.min).toLocaleString()} – ${Number(result.estimate.max).toLocaleString()}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-steel-muted">
              A copy is already saved in your portal under Invoices. Your final fee is confirmed in writing before
              any work begins.
            </p>
          </div>
        )}
        <Link
          to="/portal"
          data-testid="signup-goto-portal"
          className="mt-8 inline-flex items-center gap-2 bg-steel-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover"
        >
          Go to the sign-in page <ArrowUpRight className="h-4 w-4" />
        </Link>
      </motion.div>
    );
  }

  return (
    <div>
      {(heading || subheading) && (
        <div className="mb-8">
          {heading && <h3 className="font-display text-2xl font-medium text-steel-text">{heading}</h3>}
          {subheading && <p className="mt-2 text-sm text-steel-secondary">{subheading}</p>}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Full name *</label>
            <input data-testid="signup-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Jane Citizen" />
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input data-testid="signup-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="jane@gmail.com" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="signup-phone" value={form.phone} onChange={(e) => set("phone", maskPhone(e.target.value))} className={inputCls} placeholder="(603) 555-0100" inputMode="tel" />
          </div>
          <div>
            <label className={labelCls}>Dependents</label>
            <input data-testid="signup-dependents" type="number" min="0" max="30" value={form.dependents} onChange={(e) => set("dependents", e.target.value)} className={inputCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>What does your tax situation include?</label>
          <div className="flex flex-wrap gap-2">
            {COMPLEXITY_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`signup-item-${item.id}`}
                onClick={() => toggleItem(item.id)}
                className={`border px-3 py-1.5 text-xs transition-colors duration-300 ${
                  items.includes(item.id)
                    ? "border-steel-accent bg-steel-accent/15 text-steel-text"
                    : "border-steel-border text-steel-secondary hover:border-steel-muted hover:text-steel-text"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>States you'll need filed</label>
          <StatePicker selected={states} onChange={setStates} />
        </div>

        <div>
          <label className={labelCls}>Anything we should know?</label>
          <textarea
            data-testid="signup-message"
            rows={4}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            className={`${inputCls} resize-none`}
            placeholder="Please don't include Social Security numbers here — those move through the secure portal."
          />
        </div>

        <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-steel-secondary" data-testid="signup-tos-label">
          <input type="checkbox" checked={tos} onChange={(e) => setTos(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-purple-500" data-testid="signup-tos" />
          <span>
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="text-steel-brass underline-offset-2 hover:underline">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="text-steel-brass underline-offset-2 hover:underline">Privacy Policy</Link>, and to
            receiving my account and tax correspondence by email. Your acceptance is recorded with a timestamp.
          </span>
        </label>

        <button
          data-testid="signup-submit"
          type="submit"
          disabled={status === "sending" || !tos}
          className="group flex w-full items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-steel-accentHover disabled:opacity-50 sm:w-auto"
        >
          {status === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Creating your portal…
            </>
          ) : (
            <>
              <Mail className="h-4 w-4" /> Create My Portal & Email My Login
            </>
          )}
        </button>
        <p className="text-xs leading-relaxed text-steel-muted">
          We'll create your secure portal and email you a temporary password. You set your own password and confirm
          your details on first sign-in — no documents or SSNs needed on this page.
        </p>
      </form>
    </div>
  );
}
